import React, { useState, useEffect } from 'react';
import { apiFetch, downloadApiFile } from '../api';

export default function IngestionExport() {
  const [summary, setSummary] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [coverageBlock, setCoverageBlock] = useState(null); // null | { rowsMissing, missingFields }

  // Check for existing summary + mapping coverage on mount
  useEffect(() => {
    apiFetch(`/ingestion/summary`)
      .then(r => r.json())
      .then(data => {
        if (data.exists) setSummary(data);
      })
      .catch(() => {});

    apiFetch(`/mapping/coverage`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data && data.exists && data.rows_missing_mandatory > 0) {
          setCoverageBlock({
            rowsMissing: data.rows_missing_mandatory,
            missingFields: data.missing_fields,
          });
        }
      })
      .catch(() => {});
  }, []);

  const handleGenerate = async () => {
    const confirmed = window.confirm(
      'Generate the Frontify package now? The tool will validate every Keep asset, hold blocking rows, and replace the previous generated package for this project.'
    );
    if (!confirmed) return;
    setGenerating(true);
    setError(null);
    try {
      const res = await apiFetch(`/ingestion/generate`, { method: 'POST' });
      const data = await res.json();
      if (data.error) {
        setError(data.error);
      } else {
        setSummary(data);
      }
    } catch (err) {
      setError(err.message);
    }
    setGenerating(false);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-slate-800">Export Frontify Ingestion File</h2>
          <p className="text-sm text-slate-500 mt-1">
            Validate and export assets flagged "Keep" as a Frontify-ready CSV
          </p>
        </div>
        <button
          onClick={handleGenerate}
          disabled={generating || !!coverageBlock}
          className="px-5 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition"
        >
          {generating ? 'Generating...' : summary ? 'Regenerate Export' : 'Generate Export'}
        </button>
      </div>

      {coverageBlock && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 text-sm text-amber-800">
          <span className="font-semibold">Export blocked:</span>{' '}
          {coverageBlock.rowsMissing.toLocaleString()} asset{coverageBlock.rowsMissing !== 1 ? 's are' : ' is'} missing
          mandatory field{coverageBlock.missingFields.length !== 1 ? 's' : ''}{' '}
          ({coverageBlock.missingFields.join(', ')}).
          Return to <strong>Field Mapping</strong> to resolve before generating the export.
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-sm text-red-700">
          {error}
        </div>
      )}

      {!summary && !generating && !error && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-8 text-center">
          <div className="text-slate-500 text-sm">
            Click "Generate Export" to validate your data and create the Frontify ingestion file.
          </div>
          <div className="text-slate-400 text-xs mt-2">
            Only assets flagged as "Keep" in the Rationalize step will be included.
          </div>
        </div>
      )}

      {summary && <ValidationSummary summary={summary} />}
    </div>
  );
}


function ValidationSummary({ summary }) {
  const {
    total_source_assets, total_keep, total_valid, total_errors,
    error_types, error_rows, columns, batch_warning, batch_count = 1,
    batch_files = [], row_accounting,
  } = summary;

  const allValid = total_errors === 0;

  return (
    <div>
      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <SummaryCard
          label="Source Assets"
          value={total_source_assets?.toLocaleString()}
          sublabel="Total in CSV"
          color="slate"
        />
        <SummaryCard
          label="Flagged Keep"
          value={total_keep?.toLocaleString()}
          sublabel="Selected for migration"
          color="blue"
        />
        <SummaryCard
          label="Valid Rows"
          value={total_valid?.toLocaleString()}
          sublabel="Ready to export"
          color="green"
        />
        <SummaryCard
          label="Held for Review"
          value={total_errors?.toLocaleString()}
          sublabel={total_errors > 0 ? 'See errors below' : 'No issues found'}
          color={total_errors > 0 ? 'red' : 'green'}
        />
      </div>

      {batch_warning && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 text-sm text-amber-800">
          {batch_warning}
        </div>
      )}

      {/* Success state */}
      {allValid && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="text-green-600 text-xl">✓</div>
              <div>
                <div className="text-sm font-semibold text-green-800">
                  All {total_valid?.toLocaleString()} assets passed validation
                </div>
                <div className="text-sm text-green-700 mt-1">
                  {batch_count > 1
                    ? `${batch_count} Frontify-compliant batches are ready. Each contains no more than 50,000 assets.`
                    : `The ingestion file is ready for Frontify. It contains ${columns?.length} columns with validated formats and unique IDs.`}
                </div>
              </div>
            </div>
            <button
              onClick={() => downloadApiFile(
                '/ingestion/download',
                batch_count > 1 ? 'frontify_ingestion_package.zip' : 'frontify_ingestion.csv'
              ).catch(() => {})}
              className="flex-shrink-0 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition whitespace-nowrap"
            >
              {batch_count > 1 ? '⬇ Download Export Package' : '⬇ Download Frontify Ingestion CSV'}
            </button>
          </div>
        </div>
      )}

      {/* File info */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <h3 className="text-sm font-semibold text-slate-700 mb-3">Generated Files</h3>
        <div className="space-y-3">
          {batch_count > 1 ? (
            <FileInfo
              label={`Frontify Export Package (${batch_count} batches)`}
              path="frontify_ingestion_package.zip"
              rows={total_valid}
              description={batch_files.map(file => `${file.name}: ${file.row_count.toLocaleString()} rows`).join(' · ')}
              downloadPath="/ingestion/download"
              fallbackName="frontify_ingestion_package.zip"
            />
          ) : (
            <FileInfo
              label="Frontify Ingestion CSV"
              path={batch_files[0]?.name || 'frontify_ingestion.csv'}
              rows={total_valid}
              description="Upload this file to Frontify's bulk metadata import"
              downloadPath="/ingestion/download"
              fallbackName="frontify_ingestion.csv"
            />
          )}
          {total_errors > 0 && (
            <FileInfo
              label="Error Report"
              path="output/ingestion_errors.csv"
              rows={total_errors}
              description="Rows held due to validation errors — review and fix before re-exporting"
            />
          )}
        </div>
      </div>

      {row_accounting && (
        <div className={`rounded-lg p-4 mb-6 text-sm border ${row_accounting.balanced ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'}`}>
          <strong>{row_accounting.balanced ? 'Row accounting verified' : 'Row accounting failed'}:</strong>{' '}
          {row_accounting.source?.toLocaleString()} source = {row_accounting.exported?.toLocaleString()} exported +{' '}
          {row_accounting.held?.toLocaleString()} held + {row_accounting.intentionally_excluded?.toLocaleString()} intentionally excluded.
        </div>
      )}

      {/* Column list */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <h3 className="text-sm font-semibold text-slate-700 mb-3">
          Columns in Export ({columns?.length})
        </h3>
        <div className="flex flex-wrap gap-2">
          {(columns || []).map(col => {
            const isMandatory = ['ID', 'Filename', 'Location', 'Title'].includes(col);
            const isCustom = col.includes('(');
            return (
              <span
                key={col}
                className={`px-2 py-1 rounded text-xs font-medium ${
                  isMandatory ? 'bg-blue-100 text-blue-700' :
                  isCustom ? 'bg-purple-100 text-purple-700' :
                  'bg-slate-100 text-slate-600'
                }`}
              >
                {col}
              </span>
            );
          })}
        </div>
      </div>

      {/* Error details */}
      {total_errors > 0 && (
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex justify-between items-baseline mb-4">
            <h3 className="text-sm font-semibold text-slate-700">Validation Errors</h3>
            <span className="text-xs text-red-600 font-medium">
              {total_errors} row{total_errors > 1 ? 's' : ''} held
            </span>
          </div>

          {/* Error type summary */}
          {Object.keys(error_types || {}).length > 0 && (
            <div className="mb-4">
              <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Error Summary</div>
              {Object.entries(error_types).map(([type, count]) => (
                <div key={type} className="flex justify-between text-sm py-1 border-b border-slate-50">
                  <span className="text-slate-700">{type}</span>
                  <span className="text-red-600 font-medium">{count}</span>
                </div>
              ))}
            </div>
          )}

          {/* Error rows table */}
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase">Row</th>
                <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase">Asset</th>
                <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase">Errors</th>
              </tr>
            </thead>
            <tbody>
              {(error_rows || []).slice(0, 50).map(row => (
                <tr key={row.row_index} className="border-b border-slate-100">
                  <td className="py-2 px-3 text-slate-500">{row.row_index + 1}</td>
                  <td className="py-2 px-3 text-slate-800 font-medium truncate max-w-xs">{row.filename}</td>
                  <td className="py-2 px-3">
                    {row.errors.map((err, i) => (
                      <div key={i} className="text-xs text-red-600">{err}</div>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {total_errors > 50 && (
            <div className="text-xs text-slate-500 mt-2">
              Showing 50 of {total_errors.toLocaleString()} error rows. Full list in ingestion_errors.csv.
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function SummaryCard({ label, value, sublabel, color }) {
  const colors = {
    blue: 'border-blue-500 bg-blue-50',
    green: 'border-green-500 bg-green-50',
    red: 'border-red-500 bg-red-50',
    slate: 'border-slate-400 bg-slate-50',
  };
  return (
    <div className={`bg-white rounded-lg shadow-sm border-l-4 p-4 ${colors[color] || colors.slate}`}>
      <div className="text-sm font-medium text-slate-500">{label}</div>
      <div className="text-2xl font-bold text-slate-800 mt-1">{value}</div>
      {sublabel && <div className="text-xs text-slate-500 mt-1">{sublabel}</div>}
    </div>
  );
}


function FileInfo({ label, path, rows, description, downloadPath, fallbackName }) {
  return (
    <div className="flex items-start justify-between gap-3 p-3 bg-slate-50 rounded">
      <div className="flex items-start gap-3">
        <div className="text-slate-400 text-lg">📄</div>
        <div>
          <div className="text-sm font-medium text-slate-800">{label}</div>
          <div className="text-xs text-slate-500">{path} — {rows?.toLocaleString()} rows</div>
          <div className="text-xs text-slate-400 mt-0.5">{description}</div>
        </div>
      </div>
      {downloadPath && (
        <button
          onClick={() => downloadApiFile(downloadPath, fallbackName || 'frontify_ingestion.csv').catch(() => {})}
          className="flex-shrink-0 text-xs text-blue-600 hover:underline self-center"
        >
          ⬇ Download
        </button>
      )}
    </div>
  );
}
